import React, { useCallback, useMemo } from 'react';

import { Button } from '@mui/material';
import _ from 'lodash';

import { SECTION_FORM_CONFIG_MAP, SECTION_HREF_MAP } from '../../EditDrawer.config';
import ControlsSection from '../components/ControlsSection';
import TitleHoldersSection from '../components/TitleHoldersSection';
import { TitleHoldersSectionTestIds } from '../components/TitleHoldersSection/TitleHoldersSection.testIds';
import { getDisabledConfig } from '../Content.utils';

export const useTitleHolderRenderConfig = ({
  config,
  currentSection,
  borrowers,
  titleHolders,
  isTrustSectionVisible,
  isBplLoanPurposeType,
  mannerHeldOptions,
  isMannerHeldDisabled,
  isCompanyNameFieldVisible,
  borrowerVestingOptions,
  onTitleHolderAdd,
  onTitleHolderDelete,
  onAddTrust,
  onRemoveTrust,
  onCopyBorrowers,
  onPopulateVesting,
  formRef,
  setFormRef,
  setInFormRef,
}) => {
  const titleHoldersSection = useCallback(
    ({ disabled }) => (
      <TitleHoldersSection
        borrowers={borrowers}
        disabled={disabled}
        titleHolders={titleHolders}
        onTitleHolderAdd={onTitleHolderAdd}
        onTitleHolderDelete={onTitleHolderDelete}
      />
    ),
    [borrowers, titleHolders, onTitleHolderAdd, onTitleHolderDelete],
  );

  const onToggleTrust = useCallback(() => {
    const handler = isTrustSectionVisible ? onRemoveTrust : onAddTrust;

    handler({
      setInForm: setInFormRef.current,
      setForm: setFormRef.current,
      form: formRef.current,
    });
  }, [isTrustSectionVisible, onAddTrust, onRemoveTrust, formRef, setFormRef, setInFormRef]);

  const controlsSection = useCallback(
    ({ disabled }) => (
      <ControlsSection
        disabled={disabled}
        isBplLoanPurposeType={isBplLoanPurposeType}
        isTrustSectionVisible={isTrustSectionVisible}
        onTitleHolderAdd={onTitleHolderAdd}
        onToggleTrust={onToggleTrust}
      />
    ),
    [isBplLoanPurposeType, isTrustSectionVisible, onToggleTrust, onTitleHolderAdd],
  );

  const onPopulateVestingClick = useCallback(() => {
    onPopulateVesting({ form: formRef.current, setInForm: setInFormRef.current });
  }, [onPopulateVesting, formRef, setInFormRef]);

  const populateVestingButton = useCallback(
    ({ disabled }) => (
      <Button
        data-testid={TitleHoldersSectionTestIds.POPULATE_VESTING_BUTTON}
        disabled={disabled}
        onClick={onPopulateVestingClick}
      >
        Populate Vesting
      </Button>
    ),
    [onPopulateVestingClick],
  );

  const onCopyBorrowersClick = useCallback(
    e => onCopyBorrowers({ e, setInForm: setInFormRef.current }),
    [onCopyBorrowers, setInFormRef],
  );

  const copyBorrowersButton = useCallback(
    ({ 'data-group-name': groupName }) => (
      <Button
        className="copy-borrowers"
        data-group-name={groupName}
        data-testid={TitleHoldersSectionTestIds.COPY_BORROWERS_BUTTON}
        onClick={onCopyBorrowersClick}
      >
        Copy Borrowers
      </Button>
    ),
    [onCopyBorrowersClick],
  );

  const renderConfig = useMemo(() => {
    if (currentSection !== SECTION_HREF_MAP.TITLE_HOLDER) {
      return config;
    }

    const configOrGetter = SECTION_FORM_CONFIG_MAP[currentSection];

    if (!_.isFunction(configOrGetter)) {
      return config;
    }

    return configOrGetter({
      borrowerVestingOptions,
      mannerHeldOptions,
      isTrustSectionVisible,
      isMannerHeldDisabled,
      isCompanyNameFieldVisible,
      isBplLoanPurposeType,
      titleHoldersSection,
      controlsSection,
      populateVestingButton,
      copyBorrowersButton,
    });
  }, [
    currentSection,
    config,
    mannerHeldOptions,
    borrowerVestingOptions,
    isTrustSectionVisible,
    isMannerHeldDisabled,
    isCompanyNameFieldVisible,
    isBplLoanPurposeType,
    titleHoldersSection,
    controlsSection,
    populateVestingButton,
    copyBorrowersButton,
  ]);

  const disabledConfig = useMemo(() => getDisabledConfig(renderConfig), [renderConfig]);

  return { renderConfig, disabledConfig };
};
